import { DateTime } from "@common/data-models/complex-types/date-time";
import { Record } from '@common/data-models/records/record';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { Table } from '@common/data-models/complex-types/table';
import { ObjectTypes } from "@common/enums/object-types";
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { AttachmentList } from '@common/data-models/complex-types/attachment-list';
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { FieldDef } from "@common/data-models/service/field-def";

import { IAppService } from "@suite/i-app.service";
import { IPlaybookService } from "@playbook/interfaces/i-playbook-service";
import { Address } from "@suite/common/data-models/complex-types/address";
import { TopicRecord } from "./topic-record";

export type AssignmentCoverageType = "Text" | "Photo" | "Live Video" | "Video" | "Sound" | "Graphic" | "Interactive" | "Social" | "SocialPromotion" | "LiveVideo"

export class AssignmentRecord extends Record {
    public static playbookService: IPlaybookService;

    public initialize() {
        this.type = ObjectTypes.ASSIGNMENT;
        super.initialize();

        this.department = new MultiValue<string>();
        this.attachments = new AttachmentList();
        this.storyOwner = new MultiValue<string>();
        this.newsEditor = new MultiValue<string>();
        this.status = "";

        this.setupRequiredFieldsGroups("Assignment");
    }

    protected getExcludedFields(): { [id: string]: boolean } {
        let excluded: { [id: string]: boolean } = {};
        excluded[PSTFieldNames.PSTAssignmentSlug] = true;
        excluded[PSTFieldNames.PSTAssignmentPublishingDesk] = true;
        excluded[PSTFieldNames.PSTAssignmentCoverageType] = true;
        excluded[PSTFieldNames.PSTAssignmentCost] = true;
        excluded[PSTFieldNames.PSTTopicTag] = true;
        excluded[PSTFieldNames.PSTAssignmentStatus] = true;
        excluded[PSTFieldNames.PSTAssignmentCoverage] = true;
        excluded[PSTFieldNames.PSTExternalFiles] = true;
        excluded[PSTFieldNames.PSTApproved] = true;
        return excluded;
    }

    public topicMetadata: { guid: string, slug: string, category: string, location: string, owner: string, type: MultiValue<string> }

    public get parentGuid(): string {
        let parentGuid = this.getAs<string>("ParentGuid");
        if (!parentGuid) {
            // Extract parent guid from the path
            if(this.path) {
                parentGuid = this.path.parentCollectionGuid;
            }
        }
        return parentGuid;
    }
    public set parentGuid(value: string) { this.set("ParentGuid", value); }

    public get storyOwner(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTAssignmentOwner); }
    public set storyOwner(value: MultiValue<string>) { this.set(PSTFieldNames.PSTAssignmentOwner, value); }

    public get cost(): Table { return this.getAs<Table>(PSTFieldNames.PSTAssignmentCost); }
    public set cost(value: Table) { this.set(PSTFieldNames.PSTAssignmentCost, value); }

    public get coverage(): string { return this.getAs<string>(PSTFieldNames.PSTAssignmentCoverage); }
    public set coverage(value: string) { this.set(PSTFieldNames.PSTAssignmentCoverage, value); }

    public get newsEditor(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTAssignmentNewsEditor); }
    public set newsEditor(value: MultiValue<string>) { this.set(PSTFieldNames.PSTAssignmentNewsEditor, value); }

    public get tags(): LookupList { return this.getAs<LookupList>(PSTFieldNames.PSTTopicTag, new LookupList()); }
    public set tags(value: LookupList) { this.set(PSTFieldNames.PSTTopicTag, value); }

    public get coverageType(): AssignmentCoverageType { return this.getAs<AssignmentCoverageType>(PSTFieldNames.PSTAssignmentCoverageType); }
    public set coverageType(value: AssignmentCoverageType) { this.set(PSTFieldNames.PSTAssignmentCoverageType, value); }

    public get department(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTAssignmentPublishingDesk); }
    public set department(value: MultiValue<string>) { this.set(PSTFieldNames.PSTAssignmentPublishingDesk, value); }

    public get assignmentSlug(): string { return this.getAs<string>(PSTFieldNames.PSTAssignmentSlug); }
    public set assignmentSlug(value: string) { this.set(PSTFieldNames.PSTAssignmentSlug, value); }

    public get status(): string { return this.getAs<string>(PSTFieldNames.PSTAssignmentStatus); }
    public set status(value: string) { this.set(PSTFieldNames.PSTAssignmentStatus, value); }

    public get fileDate(): DateTime { return this.getAs<DateTime>(PSTFieldNames.PSTAssignmentFileDate); }
    public set fileDate(value: DateTime) { this.set(PSTFieldNames.PSTAssignmentFileDate, value); }

    get attachments(): AttachmentList { return this.getAs<AttachmentList>(PSTFieldNames.PSTExternalFiles); }
    set attachments(value: AttachmentList) { this.set(PSTFieldNames.PSTExternalFiles, value); }

    public get pstApproved(): boolean { return this.getAs<boolean>(PSTFieldNames.PSTApproved); }
    public set pstApproved(value: boolean) { this.set(PSTFieldNames.PSTApproved, value); }


    public get parentCategory(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentCategory, new MultiValue<string>()); }
    public set parentCategory(value: MultiValue<string>) { this.set(PSTFieldNames.PSTParentCategory, value); }


    public get parentOwner(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentOwner, new MultiValue<string>()); }
    public set parentOwner(value: MultiValue<string>) { this.set(PSTFieldNames.PSTParentOwner, value); }


    public get parentLocation(): Address { return this.getAs<Address>(PSTFieldNames.PSTParentLocation, new Address()); }
    public set parentLocation(value: Address) { this.set(PSTFieldNames.PSTParentLocation, value); }

    
    public get parentType(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentType); }
    public set parentType(value: MultiValue<string>) { this.set(PSTFieldNames.PSTParentType, value); }

    


    public getSecurityPrivsWrite(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false;
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
        }
    }

    public getSecurityPrivsRead(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
        }
    }

    private static _fieldDefsDict: { [id: string]: FieldDef } = null;
    public get fieldDefsDict(): { [id: string]: FieldDef } {
        if (!AssignmentRecord._fieldDefsDict) {
            AssignmentRecord._fieldDefsDict = {};
            for (let key in Record.globalTablesService.FieldDefsDict) {
                if (Record.globalTablesService.FieldDefsDict[key].AssignmentProp) {
                    AssignmentRecord._fieldDefsDict[key] = Record.globalTablesService.FieldDefsDict[key];
                }
            }
        }
        return AssignmentRecord._fieldDefsDict;
    }

    get getRequiredProperties(): { [id: string]: FieldDef } {
        return AssignmentRecord.playbookService.AssignmentRequiredFieldDefsDict;
    }

    public parentChanged() {
        if (this.parentGuid && this.parentRecord) {
            if (!this.topicMetadata) {
                this.topicMetadata = { guid: null, slug: null, category: null, location: null, owner: null, type: null };
            }
            this.topicMetadata.guid = this.parentRecord.guid;
            this.topicMetadata.slug = this.parentRecord.title;
            const owners = this.parentRecord.storyOwner;
            this.topicMetadata.owner = owners.map((prop)=>{
                return Record.globalTablesService.Staff[prop]?.DisplayName || prop;
            }).join(', ');

            this.topicMetadata.category = (<TopicRecord>this.parentRecord).categories.toString();
            this.topicMetadata.type = (<TopicRecord>this.parentRecord).topicType;
            this.topicMetadata.location = (<TopicRecord>this.parentRecord).location.title;
        }
    }

}
